import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * ParticlePortrait
 * ─────────────────
 * Drop-in React component that renders a photo as an interactive
 * Three.js particle system. Particles scatter away from the cursor
 * and snap back when it moves away.
 *
 * Props:
 *   imageSrc   – path/URL to the portrait image (required)
 *   width      – canvas width  (default 500)
 *   height     – canvas height (default 600)
 *   className  – extra Tailwind / CSS classes for the wrapper div
 *
 * Usage in your hero section:
 *   import ParticlePortrait from "./ParticlePortrait";
 *   <ParticlePortrait imageSrc="/your-photo.jpg" width={480} height={580} />
 */
export default function ParticlePortrait({
  imageSrc,
  width = 500,
  height = 600,
  className = "",
}) {
  const mountRef = useRef(null);

  useEffect(() => {
    const W = width;
    const H = height;
    const mount = mountRef.current;

    /* ── Renderer ─────────────────────────────────────────────── */
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(W, H);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    /* ── Scene / Camera ───────────────────────────────────────── */
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, W / H, 0.1, 1000);
    const vFov = (camera.fov * Math.PI) / 180;
    camera.position.z = (Math.max(W, H) / 2 / Math.tan(vFov / 2)) * 1.1;

    /* ── Helpers ──────────────────────────────────────────────── */
    const mouse = new THREE.Vector2(9999, 9999); // off-screen default
    const INFLUENCE_RADIUS = 80; // px in world-space
    const REPULSION_STRENGTH = 120;
    const RETURN_SPEED = 0.08;
    const PARTICLE_SPACING = 3; // sample every N pixels
    const PARTICLE_SIZE = 1.8;

    /* ── Mouse tracking (in canvas-local coords → world space) ── */
    const onMouseMove = (e) => {
      const rect = renderer.domElement.getBoundingClientRect();
      // Convert to [-W/2, W/2] × [-H/2, H/2] world coords
      mouse.x = ((e.clientX - rect.left) / rect.width - 0.5) * W;
      mouse.y = -((e.clientY - rect.top) / rect.height - 0.5) * H;
    };
    const onMouseLeave = () => {
      mouse.set(9999, 9999);
    };
    mount.addEventListener("mousemove", onMouseMove);
    mount.addEventListener("mouseleave", onMouseLeave);

    /* ── Build particle system from image ─────────────────────── */
    let animId;
    let positions, origins, velocities, particleCount;
    let particles;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageSrc;

    img.onload = () => {
      /* Sample image pixels on a 2-D canvas */
      const sW = Math.floor(W / PARTICLE_SPACING);
      const sH = Math.floor(H / PARTICLE_SPACING);

      const offscreen = document.createElement("canvas");
      offscreen.width = sW;
      offscreen.height = sH;
      const ctx = offscreen.getContext("2d");
      // Flip to greyscale-ish style: draw normal then desaturate via CSS filter trick
      ctx.filter = "grayscale(100%) contrast(1.1) brightness(1.05)";
      const imgAspect = img.width / img.height;
      const targetAspect = sW / sH;
      if (imgAspect > targetAspect) {
        const drawH = sW / imgAspect;
        ctx.drawImage(img, 0, (sH - drawH) / 2, sW, drawH);
      } else {
        const drawW = sH * imgAspect;
        ctx.drawImage(img, (sW - drawW) / 2, 0, drawW, sH);
      }

      const data = ctx.getImageData(0, 0, sW, sH).data;

      /* Collect only "bright enough" pixels so particles follow the portrait */
      const pts = [];
      const cols = [];
      for (let y = 0; y < sH; y++) {
        for (let x = 0; x < sW; x++) {
          const i = (y * sW + x) * 4;
          const r = data[i],
            g = data[i + 1],
            b = data[i + 2],
            a = data[i + 3];
          if (a < 20) continue;
          const brightness = (r + g + b) / 3;
          if (brightness < 15) continue; // skip near-black background

          // World coords centred at 0,0
          const wx = (x / sW - 0.5) * W;
          const wy = -(y / sH - 0.5) * H;
          pts.push(wx, wy, 0);
          cols.push(r / 255, g / 255, b / 255);
        }
      }

      particleCount = pts.length / 3;
      positions = new Float32Array(pts);
      origins = new Float32Array(pts); // target (resting) positions
      velocities = new Float32Array(particleCount * 3); // vx, vy, vz

      /* Scatter particles from random positions at startup */
      for (let i = 0; i < particleCount * 3; i += 3) {
        positions[i] = (Math.random() - 0.5) * W * 1.5;
        positions[i + 1] = (Math.random() - 0.5) * H * 1.5;
        positions[i + 2] = (Math.random() - 0.5) * 200;
      }

      /* Geometry */
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geo.setAttribute(
        "color",
        new THREE.BufferAttribute(new Float32Array(cols), 3),
      );

      /* Material */
      const mat = new THREE.PointsMaterial({
        size: PARTICLE_SIZE,
        vertexColors: true,
        transparent: true,
        opacity: 0.92,
        sizeAttenuation: true,
      });

      particles = new THREE.Points(geo, mat);
      scene.add(particles);

      /* ── Animation loop ──────────────────────────────────────── */
      const animate = () => {
        animId = requestAnimationFrame(animate);

        const pos = particles.geometry.attributes.position;

        for (let i = 0; i < particleCount; i++) {
          const ix = i * 3,
            iy = ix + 1,
            iz = ix + 2;

          const px = positions[ix],
            py = positions[iy];

          const dx = px - mouse.x;
          const dy = py - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < INFLUENCE_RADIUS) {
            // Repel
            const force = (INFLUENCE_RADIUS - dist) / INFLUENCE_RADIUS;
            const angle = Math.atan2(dy, dx);
            velocities[ix] +=
              Math.cos(angle) * force * REPULSION_STRENGTH * 0.04;
            velocities[iy] +=
              Math.sin(angle) * force * REPULSION_STRENGTH * 0.04;
          }

          // Spring back to origin
          velocities[ix] += (origins[ix] - px) * RETURN_SPEED;
          velocities[iy] += (origins[iy] - positions[iy]) * RETURN_SPEED;
          velocities[iz] += (origins[iz] - positions[iz]) * RETURN_SPEED;

          // Damping
          velocities[ix] *= 0.82;
          velocities[iy] *= 0.82;
          velocities[iz] *= 0.82;

          positions[ix] += velocities[ix];
          positions[iy] += velocities[iy];
          positions[iz] += velocities[iz];
        }

        pos.needsUpdate = true;
        renderer.render(scene, camera);
      };

      animate();
    };

    img.onerror = () => {
      console.error("ParticlePortrait: failed to load image →", imageSrc);
    };

    /* ── Cleanup ─────────────────────────────────────────────── */
    return () => {
      cancelAnimationFrame(animId);
      mount.removeEventListener("mousemove", onMouseMove);
      mount.removeEventListener("mouseleave", onMouseLeave);
      if (particles) {
        particles.geometry.dispose();
        particles.material.dispose();
        scene.remove(particles);
      }
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [imageSrc, width, height]);

  return (
    <div
      ref={mountRef}
      className={`cursor-none ${className}`}
      style={{ width, height }}
    />
  );
}
